import React, { useState } from 'react';
import { Organization } from '../../types/organizations';

interface DashboardTabProps {
  organization: Organization;
}

interface AccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  accounts: Array<{
    id: string;
    name: string;
    past_due_amount: number;
    past_due_inv_count: number;
  }>;
}

const AccountModal: React.FC<AccountModalProps> = ({ isOpen, onClose, title, accounts }) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Past Due Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice Count
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {accounts.map((account, index) => (
                  <tr key={account.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {account.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(account.past_due_amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {account.past_due_inv_count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="mt-4 flex justify-end">
            <button
              onClick={onClose}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DashboardTab: React.FC<DashboardTabProps> = ({ organization }) => {
  const creditMetadata = organization.credit_controlling_metadata;
  const [showTop10Modal, setShowTop10Modal] = useState(false);
  const [showTop3Modal, setShowTop3Modal] = useState(false);

  if (!creditMetadata) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Credit Controlling Data Not Available
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Credit controlling metadata is not available for this organization. This may be due to insufficient invoice data or integration issues.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPercentage = (percentage: number) => {
    return `${percentage.toFixed(1)}%`;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Cash Stuck</h2>
        <p className="mt-2 text-sm text-gray-600">
          Past due receivables overview of the dunning customers.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Past Due Receivable */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-red-100 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total PD
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(creditMetadata.total_past_due_receivable)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* DPD 0-7 Payment Performance */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-teal-100 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7 20h10a2 2 0 002-2V6a2 2 0 00-2-2H7a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    DPD Trend (0-7)
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatPercentage(creditMetadata.dpd_0_7 || 0)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Top 10 Largest Past Due Accounts Amount */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-orange-100 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0H9m0 0h6" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total PD (<button 
                      onClick={() => setShowTop10Modal(true)}
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      Largest 10 Acc
                    </button>)
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(creditMetadata.top_10_largest_past_due_accounts_amount)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Top 10 Largest Past Due Accounts Percentage */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total PD % (Largest 10 Acc)
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatPercentage(creditMetadata.top_10_largest_past_due_accounts_percentage)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Past Due Invoice Count from Top 10 */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    PD Invoices (Largest 10 Acc)
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {creditMetadata.past_due_invoice_count_from_top_10_largest_past_due_accounts}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Top 3 Largest Past Due Accounts Amount */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0H9m0 0h6" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total PD (<button 
                      onClick={() => setShowTop3Modal(true)}
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      Largest 3 Acc
                    </button>)
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(creditMetadata.top_3_largest_past_due_accounts_amount)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Top 3 Largest Past Due Accounts Percentage */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total PD % (Largest 3 Acc)
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatPercentage(creditMetadata.top_3_largest_past_due_accounts_percentage)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Past Due Invoice Count from Top 3 */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-indigo-100 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                  PD Invoices (Largest 3 Acc)
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {creditMetadata.past_due_invoice_count_from_top_3_largest_past_due_accounts}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* DSO Last 90 Days */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-cyan-100 rounded-md flex items-center justify-center">
                  <svg className="w-5 h-5 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    DSO (Last 90 Days)
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {creditMetadata.dso_last_90_days ? `${creditMetadata.dso_last_90_days.toFixed(1)} days` : 'N/A'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Insights */}
      <div className="mt-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Credit Risk Insights
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Concentration Risk</h4>
                <p className="text-sm text-gray-900">
                  {creditMetadata.top_10_largest_past_due_accounts_percentage > 80 
                    ? "High concentration risk - top 10 accounts represent a significant portion of past due receivables."
                    : creditMetadata.top_10_largest_past_due_accounts_percentage > 60
                    ? "Moderate concentration risk - consider diversifying customer base."
                    : "Low concentration risk - past due receivables are well distributed."
                  }
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Collection Priority</h4>
                <p className="text-sm text-gray-900">
                  {creditMetadata.past_due_invoice_count_from_top_10_largest_past_due_accounts > 50
                    ? "High priority - focus on top accounts with multiple past due invoices."
                    : creditMetadata.past_due_invoice_count_from_top_10_largest_past_due_accounts > 20
                    ? "Medium priority - monitor top accounts closely."
                    : "Low priority - manageable number of past due invoices from top accounts."
                  }
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">DSO Performance</h4>
                <p className="text-sm text-gray-900">
                  {creditMetadata.dso_last_90_days && creditMetadata.dso_last_90_days > 60
                    ? "High DSO - consider tightening credit terms or improving collection processes."
                    : creditMetadata.dso_last_90_days && creditMetadata.dso_last_90_days > 30
                    ? "Moderate DSO - monitor trends and optimize collection strategies."
                    : creditMetadata.dso_last_90_days
                    ? "Low DSO - excellent cash flow management and collection efficiency."
                    : "DSO data not available for analysis."
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AccountModal
        isOpen={showTop10Modal}
        onClose={() => setShowTop10Modal(false)}
        title="Top 10 Largest Past Due Accounts"
        accounts={creditMetadata.top_10_accounts}
      />

      <AccountModal
        isOpen={showTop3Modal}
        onClose={() => setShowTop3Modal(false)}
        title="Top 3 Largest Past Due Accounts"
        accounts={creditMetadata.top_3_accounts}
      />
    </div>
  );
};

export default DashboardTab;
